import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ButtonVariant, ButtonComponent, Size, BadgeColor, BadgeType } from '@circlek-global/lds-components';

import { GtmCustomEventNameEnum } from '~components/gtm-script/gtm-custom-event-name.enum';
import { GtmCustomEventService } from '~components/gtm-script/gtm-custom-event.service';
import { QUICK_FILTERS, QuickFilterConfig } from '~models/quick-filter.model';
import { StationsFilters } from '~models/station.model';
import { TranslatePipe } from '~pipes/translate.pipe';
import { StationsService } from '~services/station.service';
import { BusinessUnit, DEFAULT_BUSINESS_UNIT } from '~shared/business-unit';
import { StationDataStore } from '~state/station-data/station-data.store';
import { getLoyaltyProgramData, LoyaltyProgram } from '~utils/get-loyalty-programm-data';

@Component({
  selector: 'app-quick-filter-pills',
  templateUrl: './quick-filter-pills.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ButtonComponent],
})
export class QuickFilterPillsComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  protected readonly BadgeColor = BadgeColor;
  protected readonly BadgeType = BadgeType;
  protected readonly Size = Size;
  protected readonly bu = (this.activatedRoute.snapshot.queryParams['bu'] ?? DEFAULT_BUSINESS_UNIT) as BusinessUnit;
  protected readonly loyaltyProgramData = signal<LoyaltyProgram>(getLoyaltyProgramData(this.bu)).asReadonly();
  protected readonly ButtonVariant = ButtonVariant;
  protected readonly quickFilters = QUICK_FILTERS;
  protected readonly activeFilters = computed(() => {
    const filters = this.stationsService.filters();

    return {
      openNow: filters.openNow,
      services: filters.services,
      fuels: filters.fuels,
      loyaltyPrograms: filters.loyaltyPrograms ?? [],
    };
  });
  protected readonly filterActiveStates = computed(() => {
    const filters = this.activeFilters();
    const allFuelNames = this.fuels().map((f) => f.name);
    const allServiceNames = this.services().map((s) => s.name);
    const activeQuickFiltersMap = new Map(
      this.quickFilters.map((filter) => [
        filter.id,
        this.evaluateFilterActive(filter, filters, allFuelNames, allServiceNames),
      ]),
    );

    const activeQuickFiltersList: string[] = [];

    activeQuickFiltersMap.forEach((isActive, filterId) => {
      if (isActive) {
        activeQuickFiltersList.push(filterId);
      }
    });

    if (activeQuickFiltersList.length > 0) {
      this.gtmCustomEventService.trackEvent(
        GtmCustomEventNameEnum.QuickFilters,
        this.getQuickFiltersForCustomEvent(activeQuickFiltersList),
      );
    }

    return activeQuickFiltersMap;
  });
  private readonly stationDataStore = inject(StationDataStore);
  private readonly stationsService = inject(StationsService);
  private readonly fuels = this.stationDataStore.fuels;
  private readonly services = this.stationDataStore.services;

  constructor() {
    effect(() => {
      if (this.services().length > 0 && this.fuels().length > 0) {
        untracked(() => {
          this.applyQuickFiltersFromQueryParams();
        });
      }
    });
  }

  onToggleFilter(filter: QuickFilterConfig): void {
    const newFilters = this.getToggledQuickFilter(filter);

    this.stationsService.filters.set(newFilters);
    this.stationsService.fetchStations().subscribe();
  }

  private getQuickFiltersForCustomEvent(filterIds: string[]): Record<string, string> {
    const quickFilterNames = filterIds
      .map((id) => {
        return id
          .split(/(?=[A-Z])/)
          .join(' ')
          .toLowerCase();
      })
      .join(', ');

    return {
      quick_filters: quickFilterNames,
    };
  }

  private getToggledQuickFilter(filter: QuickFilterConfig): StationsFilters {
    const currentFilters = this.stationsService.filters();
    const isCurrentlyActive = this.filterActiveStates().get(filter.id) ?? false;

    const newFilters: StationsFilters = { ...currentFilters };

    switch (filter.filterType) {
      case 'openNow':
        newFilters.openNow = !isCurrentlyActive;
        break;
      case 'service':
        newFilters.services = this.getToggledServiceFilter(
          currentFilters.services,
          isCurrentlyActive,
          filter.filterValues,
        );
        break;
      case 'allFuels':
        newFilters.fuels = this.getToggledAllFuelsFilter(isCurrentlyActive);
        break;
      case 'servicePattern':
        newFilters.services = this.getToggledServicePatternFilter(
          currentFilters.services,
          isCurrentlyActive,
          filter.filterValues[0],
        );
        break;
      case 'loyaltyPrograms':
        newFilters.loyaltyPrograms = this.getToggledLoyaltyProgramFilter(
          isCurrentlyActive,
          newFilters.loyaltyPrograms ?? [],
        );

        break;

      default:
        break;
    }

    return newFilters;
  }

  private applyQuickFiltersFromQueryParams(): void {
    const filters = this.activatedRoute.snapshot.queryParams['filters'];

    const loyaltyProgramName = this.loyaltyProgramData().name;

    if (!filters) {
      return;
    }

    const filterNames: string[] = filters.split(',');

    filterNames.forEach((filter) => {
      const quickFilterConfig =
        filter === loyaltyProgramName
          ? this.quickFilters.find((f) => f.filterValues.includes(filter))
          : this.quickFilters.find((f) => f.id === filter);

      if (!quickFilterConfig) {
        console.warn('Unsupported quick filter:', filter);

        return;
      }

      const newFilters = this.getToggledQuickFilter({ ...quickFilterConfig });

      this.stationsService.filters.set(newFilters);
    });

    this.stationsService.fetchStations().subscribe();
  }
  private getToggledLoyaltyProgramFilter(isActive: boolean, loyaltyProgramsArray: string[]): string[] {
    return isActive ? [] : [...loyaltyProgramsArray, this.loyaltyProgramData().request];
  }

  private getToggledServiceFilter(currentServices: string[], isActive: boolean, filterValues: string[]): string[] {
    return isActive
      ? currentServices.filter((s) => !filterValues.includes(s))
      : [...currentServices, ...filterValues.filter((v) => !currentServices.includes(v))];
  }

  private getToggledAllFuelsFilter(isActive: boolean): string[] {
    const allFuelNames = this.fuels().map((f) => f.name);

    return isActive ? [] : allFuelNames;
  }

  private getToggledServicePatternFilter(
    currentServices: string[],
    isActive: boolean,
    pattern: string | undefined,
  ): string[] {
    if (!pattern) {
      return currentServices;
    }

    const lowerPattern = pattern.toLowerCase();
    const matchingServices = this.services()
      .filter((s) => s.name.toLowerCase().includes(lowerPattern))
      .map((s) => s.name);

    return isActive
      ? currentServices.filter((s) => !matchingServices.includes(s))
      : [...currentServices, ...matchingServices.filter((s) => !currentServices.includes(s))];
  }

  private evaluateFilterActive(
    filter: QuickFilterConfig,
    filters: { openNow: boolean; services: string[]; fuels: string[]; loyaltyPrograms: string[] },
    allFuelNames: string[],
    allServiceNames: string[],
  ): boolean {
    switch (filter.filterType) {
      case 'openNow':
        return filters.openNow;
      case 'service':
        return filter.filterValues.every((v) => filters.services.includes(v));
      case 'allFuels':
        return allFuelNames.length > 0 && allFuelNames.every((fuel) => filters.fuels.includes(fuel));

      case 'servicePattern': {
        const pattern = filter.filterValues[0]?.toLowerCase();

        if (!pattern) {
          return false;
        }

        const matchingServices = allServiceNames.filter((s) => s.toLowerCase().includes(pattern));

        return matchingServices.length > 0 && matchingServices.some((s) => filters.services.includes(s));
      }

      case 'loyaltyPrograms':
        return filters.loyaltyPrograms.includes(this.loyaltyProgramData().request);

      default:
        return false;
    }
  }
}
